import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { staffApi, type StaffRow } from '../lib/api'
import { dayMonth } from '../lib/money'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { Sheet } from '../components/Sheet'

type Draft = { id?: string; phone: string; name: string; role: 'Owner' | 'Staff' }

export function StaffScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)

  const [rows, setRows] = useState<StaffRow[] | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      setRows(await staffApi.list())
    } catch (e) {
      say(e instanceof Error ? e.message : 'Không tải được danh sách')
      setRows([])
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function save() {
    if (!draft) return
    setBusy(true)
    try {
      await staffApi.save(draft)
      setDraft(null)
      await load()
      say('Đã lưu')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  async function remove(row: StaffRow) {
    setBusy(true)
    try {
      await staffApi.remove(row.id)
      setDraft(null)
      await load()
      say(`Đã bỏ ${row.name}`)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa bỏ được')
    }
    setBusy(false)
  }

  return (
    <div className="screen">
      <AppBar
        title="Người làm"
        sub={rows ? `${rows.length} người` : undefined}
        onBack={() => nav('/settings')}
      />

      <div className="body">
        <p className="hint">
          Khai số điện thoại người làm ở đây. Lần đầu họ đăng nhập bằng chính số đó là vào thẳng
          quán này — không cần mã mời, không cần bạn cầm máy làm giúp.
        </p>

        <div className="list">
          {(rows ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              className="row-btn"
              onClick={() =>
                setDraft({
                  id: r.id,
                  phone: r.phone,
                  name: r.name,
                  role: r.role === 'Owner' ? 'Owner' : 'Staff',
                })
              }
            >
              <span className="row-icon" aria-hidden="true">
                {r.role === 'Owner' ? '👑' : '🧑‍🍳'}
              </span>
              <span className="row-text">
                <b>
                  {r.name}
                  {r.isMe && <em className="me-tag"> · bạn</em>}
                </b>
                <small className="num">
                  {r.phone}
                  {r.lastSeenAt ? ` · vào lần cuối ${dayMonth(r.lastSeenAt)}` : ' · chưa đăng nhập lần nào'}
                </small>
              </span>
              <span className={`role-tag ${r.role === 'Owner' ? 'owner' : ''}`}>
                {r.role === 'Owner' ? 'Chủ quán' : 'Bán hàng'}
              </span>
            </button>
          ))}
        </div>

        <div className="card">
          <h2 className="section-label">Người bán hàng làm được gì</h2>
          <div className="perm-list">
            <div className="perm yes">Bán hàng, thu tiền, in bill</div>
            <div className="perm yes">Ghi phiếu nhập hàng</div>
            <div className="perm yes">Mở ca, đếm tiền, chốt ca</div>
            <div className="perm yes">Tra hoá đơn trong ngày, huỷ kèm lý do</div>
            <div className="perm no">Xem doanh thu tháng, quý, số thuế phải nộp</div>
            <div className="perm no">Xem hoá đơn của ngày khác</div>
            <div className="perm no">Sửa giá, sửa món, sửa sơ đồ bàn</div>
            <div className="perm no">Sửa thông tin quán, tài khoản ngân hàng</div>
          </div>
          <p className="note">
            Những mục gạch chéo bị chặn ở máy chủ, không phải chỉ ẩn nút — nhân viên có gọi thẳng
            vào hệ thống cũng không lấy được.
          </p>
        </div>
      </div>

      <div className="dock">
        <button
          type="button"
          className="btn btn-brand btn-lg grow"
          onClick={() => setDraft({ phone: '', name: '', role: 'Staff' })}
        >
          + Thêm người làm
        </button>
      </div>

      {draft && (
        <Sheet
          title={draft.id ? draft.name : 'Thêm người làm'}
          onClose={() => setDraft(null)}
        >
          <label className="field">
            <span>Tên</span>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Chị Hà"
              maxLength={120}
              autoFocus={!draft.id}
            />
          </label>

          <label className="field">
            <span>Số điện thoại</span>
            <input
              type="tel"
              inputMode="tel"
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              placeholder="0912 345 678"
            />
          </label>

          <div className="field">
            <span>Vai trò</span>
            <div className="seg">
              <button
                type="button"
                aria-pressed={draft.role === 'Staff'}
                onClick={() => setDraft({ ...draft, role: 'Staff' })}
              >
                🧑‍🍳 Bán hàng
              </button>
              <button
                type="button"
                aria-pressed={draft.role === 'Owner'}
                onClick={() => setDraft({ ...draft, role: 'Owner' })}
              >
                👑 Chủ quán
              </button>
            </div>
            <p className="note">
              {draft.role === 'Owner'
                ? 'Chủ quán xem được hết: doanh thu, thuế, và sửa được mọi thứ.'
                : 'Người bán hàng chỉ bán và ghi chi, không xem được doanh thu hay thuế.'}
            </p>
          </div>

          <div className="sheet-actions">
            {draft.id && !rows?.find((r) => r.id === draft.id)?.isMe && (
              <button
                type="button"
                className="btn btn-danger-ghost"
                onClick={() => {
                  const row = rows?.find((r) => r.id === draft.id)
                  if (row) void remove(row)
                }}
                disabled={busy}
              >
                Bỏ
              </button>
            )}
            <button
              type="button"
              className="btn btn-brand grow"
              onClick={save}
              disabled={busy || !draft.name.trim() || draft.phone.trim().length < 9}
            >
              {busy ? 'Đang lưu…' : 'Lưu'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}
